import { AppText } from './AppText';

export type NoticeTone = 'info' | 'hint' | 'error';

/**
 * design-system/06-Components/08-Lists-And-States.md §4.
 * Mirrors android/…/Components/InlineNotice.kt.
 *
 * A failure is presented inline, never as a dialog in child mode, and **never in red to a child** — child
 * mode uses `info`. `error` exists for parent mode only.
 */
export function InlineNotice({
  text,
  tone = 'info',
  correlationId = null,
}: {
  text: string;
  tone?: NoticeTone;
  correlationId?: string | null;
}) {
  const palette = {
    info: { surface: 'var(--surface-info)', border: 'var(--border-default)', content: 'var(--text-primary)' },
    hint: { surface: 'var(--surface-hint)', border: 'var(--border-default)', content: 'var(--text-on-hint)' },
    error: { surface: 'var(--surface-error)', border: 'var(--text-error)', content: 'var(--text-error)' },
  }[tone];

  return (
    <div
      // `status` rather than `alert`: a hint should be announced without interrupting what the child is
      // doing, and an interruption is exactly what `alert` is for.
      role="status"
      style={{
        width: '100%',
        background: palette.surface,
        border: `1px solid ${palette.border}`,
        borderRadius: 'var(--radius-l)',
        padding: 'var(--space-4)',
      }}
    >
      <AppText color={palette.content}>{text}</AppText>

      {correlationId !== null && tone === 'error' ? (
        <AppText style="caption" color="var(--text-secondary)" sx={{ marginTop: 'var(--space-1)' }}>
          correlation id {correlationId}
        </AppText>
      ) : null}
    </div>
  );
}
